import React from 'react';
import type {SetRequired} from 'type-fest';
import {splitEventHandler} from '@netx/core-next/utils';
import {DateField, type DateFieldProps} from '@netx/core-components/DateField';
import {useConstCallback} from '@netx/core-components/hooks/useEvent';
import {useSharedRef} from '@netx/core-components/hooks/useSharedRef';
import {useFormInput} from '../hooks';
import {useErrorFocus} from '../hooks/useErrorFocus';
import type {WithLegacyCommonFormFieldProps} from '../types';

export interface LegacyFormDateFieldProps
	extends WithLegacyCommonFormFieldProps<SetRequired<DateFieldProps, 'name'>> {}

export const LegacyFormDateFieldPrimitive = (
	{
		error,
		form,
		formFieldOptions,
		inputRef,
		onChange,
		...props
	}: LegacyFormDateFieldProps,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	const innerInputRef = useSharedRef<HTMLInputElement | null>(null, [inputRef]);
	const _error = typeof error === 'string' ? error : error?.message;

	useFormInput(props.name, props.value, form, {
		required: props.required,
		...formFieldOptions,
	});

	useErrorFocus({
		error: _error,
		inputRef: innerInputRef,
	});

	const handleChange = useConstCallback(
		splitEventHandler(onChange, (event, value) => {
			form?.setValue(props.name, value);
		}),
	);

	return (
		<DateField
			ref={ref}
			{...props}
			error={_error}
			inputRef={innerInputRef}
			onChange={handleChange}
		/>
	);
};

/**
 * Legacy form wrapper for DateField
 * @deprecated
 */
export const LegacyFormDateField = React.forwardRef(
	LegacyFormDateFieldPrimitive,
);

export default LegacyFormDateField;
